'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { useEffect, useState } from 'react';

import ErrorBoundaryIcon from '../../src/asset/svg/error-boundary-icon.svg';
import { addRenderingErrorToRum } from '../utils/error/addRenderingErrorToRum';
import useChunkLoadErrorRecovery from '../utils/hook/useChunkLoadErrorRecovery';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  const { isChunkLoadError, isRecovering } = useChunkLoadErrorRecovery(error);

  /**
   * 1초 안에 3번 연속 클릭 시 localStorage 초기화
   */
  const handleInitLocalStorage = () => {
    try {
      const currentTime = new Date().getTime();

      // 마지막 클릭으로부터 1초 이내에 클릭된 경우에만 카운트 증가
      if (currentTime - lastClickTime < 1000) {
        setClickCount((prev) => prev + 1);
      } else {
        setClickCount(1);
      }

      setLastClickTime(currentTime);

      if (clickCount === 2) {
        localStorage.clear();
        window.alert('초기화가 정상적으로 완료되었습니다.');
        setClickCount(0);
        window.location.reload();
      }
    } catch (e) {
      console.error('handleInitLocalStorage:', e);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: 기본 의존성 유지
  useEffect(() => {
    // 청크 로드 실패는 useChunkLoadErrorRecovery가 자동 복구 처리하므로 로깅을 생략한다.
    if (!isChunkLoadError) {
      // Datadog Logs 에러 로깅
      try {
        datadogLogs.logger.error('[global-error] 서비스 오류 발생', {
          errorMessage: error.message,
          errorName: error.name,
          digest: error.digest,
          stack: error.stack,
        });
      } catch (e) {
        console.error('Datadog Logs 에러 로깅 실패:', e);
      }

      addRenderingErrorToRum({ error, source: 'global-error.tsx' });
    }

    return () => {
      reset();
    };
  }, []);

  // 청크 에러 복구 진행 중에는 "서비스 오류" 화면 플래시를 막기 위해 빈 화면을 렌더한다.
  // global-error는 루트 레이아웃을 대체하므로 null 대신 html/body 골격을 유지해야 한다.
  if (isRecovering) {
    return (
      <html lang="ko">
        <body />
      </html>
    );
  }

  return (
    <html lang="ko">
      <body>
        <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center">
          <div className="flex flex-col items-center space-y-28px">
            <img
              src={ErrorBoundaryIcon.src}
              alt="에러 발생"
              onClick={handleInitLocalStorage}
            />

            <div className="space-y-12px">
              <h2 className="text-26px font-bold text-gray-800">
                서비스 오류 발생
              </h2>
              <h5 className="text-center text-16px leading-6 text-gray-800">
                불편을 드려 죄송합니다.
                <br />
                고객센터로 문의해주세요.
              </h5>
            </div>

            <div className="flex space-x-10px">
              <button
                onClick={() => window.history.back()}
                className="h-56px w-108px rounded-10px bg-gray-600"
                type="button"
              >
                <h5 className="text-16px font-semibold text-white">이전</h5>
              </button>
            </div>

            {error.message && (
              <p className="mt-16px text-12px text-gray-500">{error.digest}</p>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
